import type { Vector3 } from "three";
import { Ioc } from "@core/ioc/ioc";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import { computeOrientedBounds, type OrientedBox } from "./orientedBounds";

/**
 * `snapshotMeasure.ts` — the one place a size or a height is derived from a
 * part's MESHES, shared by the Measure tool's readout and the AI's dimension
 * answers.
 *
 * It exists so both give the same number. A spoken figure that disagreed with
 * the visual drawn beside it would be worse than no figure at all, so both come
 * from the same {@link computeOrientedBounds}: the SIZE from the box's extents,
 * and the CLEARANCE from `OrientedBox.lowestPoint`.
 *
 * ⚠ The clearance used to be the lowest CORNER of that box, and the corner is
 * not on the part. The box encloses every vertex, so on a rotated part its
 * lowest corner sits below the lowest point of the mesh — which UNDERSTATED the
 * height above ground and anchored the drawn ruler in mid-air beneath the part.
 * An axis-aligned part is unaffected, which is why it went unnoticed. Do not
 * put the corner back; the mesh minimum is exact (a triangle mesh attains its
 * minimum world-Y at a vertex).
 *
 * {@link heightAboveGround} is drawn too: `BoundingBoxToolController` runs a
 * dashed plumb line from that same `lowestPoint` to {@link GROUND_PLANE_Y}, so
 * the span on screen is the figure in the answer. It reads the field rather
 * than re-deriving it, and imports the datum from this file, so the two cannot
 * drift.
 *
 * Lives in `runtime/` because it needs the Three.js node behind a snapshot id;
 * callers in `models/` and `ai/` reach it through here rather than importing
 * Three themselves, the same seam `focusSnapshots` uses.
 */

/**
 * The datum for every height answer: Campfire's world Y origin, where the
 * anchor's dashed line starts and just above which the floor grid is drawn
 * (`sceneEnvironmentRenderer`). Models resting ON the ground — floor mats,
 * pallets — are content, never the datum.
 */
export const GROUND_PLANE_Y = 0;

/**
 * Below this the part is sitting ON the ground, and the height-above-ground
 * ruler is not drawn: it would be a dot under a "0.00 cm" pill, which is noise
 * standing in for "nothing to show".
 *
 * Lives here rather than in the renderer because there are two readers and they
 * must agree — `BoundingBoxToolController` decides whether to DRAW the line, and
 * `Get_Part_Dimensions` reports to the agent whether one is on screen. Two
 * copies would let the tool promise a visual the renderer had suppressed.
 */
export const MIN_GROUND_RULER_SPAN_M = 1e-4;

export interface SnapshotMeasurement {
  /** Size along the part's own X / Y / Z axes, in METRES — the drawn pills. */
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  /**
   * Perpendicular distance from {@link GROUND_PLANE_Y} to the lowest point of
   * the part's MESHES, in metres. Negative when the part dips below the ground.
   */
  readonly heightAboveGround: number;
  /**
   * Perpendicular distance from {@link GROUND_PLANE_Y} to the part's
   * horizontal centreline, in metres: the level midway between the lowest and
   * highest points of its meshes. For a part lying level, such as a shaft or a
   * roller, that is the height of its axis. Derived by {@link centerlineAnchor}
   * so the drawn ruler starts at exactly this height.
   */
  readonly centerlineHeightAboveGround: number;
  /**
   * Perpendicular distance from {@link GROUND_PLANE_Y} to the HIGHEST point of
   * the part's meshes, in metres — `OrientedBox.highestPoint`, the same vertex
   * the `toplineRuler` starts from. The datum for overhead clearances and
   * maximum mounting heights.
   */
  readonly toplineHeightAboveGround: number;
}

export type SnapshotMeasureFailure = "viewport-unavailable" | "not-rendered" | "no-geometry";

export type SnapshotMeasureResult = SnapshotMeasurement | SnapshotMeasureFailure;

export function isMeasureFailure(
  result: SnapshotMeasureResult,
): result is SnapshotMeasureFailure {
  return typeof result === "string";
}

export function measureSnapshot(snapshotId: string): SnapshotMeasureResult {
  if (!Ioc.isRegistered<PickingService>(PICKING_SERVICE)) return "viewport-unavailable";
  const node = Ioc.resolve<PickingService>(PICKING_SERVICE).resolveSnapshotNode(snapshotId);
  if (!node) return "not-rendered";

  const obb = computeOrientedBounds(node);
  if (!obb) return "no-geometry";

  return {
    width: obb.extents.x * 2,
    height: obb.extents.y * 2,
    depth: obb.extents.z * 2,
    heightAboveGround: obb.lowestPoint.y - GROUND_PLANE_Y,
    centerlineHeightAboveGround: centerlineY(obb) - GROUND_PLANE_Y,
    toplineHeightAboveGround: obb.highestPoint.y - GROUND_PLANE_Y,
  };
}

/**
 * Where the centreline ruler starts: the box centre's horizontal position, at
 * the centreline height. The box centre puts the line through the middle of
 * the part rather than at an arbitrary vertex; only its HEIGHT is the figure,
 * and that comes from the mesh extremes, so a tilted part's box corners never
 * move it.
 */
export function centerlineAnchor(obb: OrientedBox, target: Vector3): Vector3 {
  return target.set(obb.center.x, centerlineY(obb), obb.center.z);
}

function centerlineY(obb: OrientedBox): number {
  return (obb.lowestPoint.y + obb.highestPoint.y) / 2;
}
